import React from 'react'
import { useDashboard } from '../DashboardContext'
import type { BusinessInfo } from '../../../types'

type FieldCheck = { label: string; filled: boolean; blocking?: boolean }

function checkFields(info: BusinessInfo): FieldCheck[] {
  return [
    { label: 'Business type',      filled: !!info.businessType?.trim(),                blocking: true  },
    { label: 'Lapse window',        filled: info.lapseWindowDays != null,               blocking: true  },
    { label: 'Phone',               filled: !!info.phone?.trim()                                        },
    { label: 'Location',            filled: !!info.location?.trim()                                     },
    { label: 'Writing style',       filled: !!info.brandVoice?.trim()                                   },
    { label: 'Specialty summary',   filled: !!info.specialtySummary?.trim()                             },
    { label: 'Booking URL',         filled: !!info.bookingUrl?.trim()                                   },
    { label: 'Business hours',      filled: !!info.hoursSummary?.trim()                                 },
    { label: 'Website URL',         filled: !!info.websiteUrl?.trim()                                   },
    { label: 'Email instructions',  filled: !!info.emailGenerationInstructions?.trim()                  },
    { label: 'Special offer',       filled: !!info.defaultOfferText?.trim()                             },
    { label: 'Sender name',         filled: !!info.fromDisplayName?.trim()                              },
  ]
}

export function ProfileSetupBanner() {
  const { businessInfo, setActivePage } = useDashboard()
  const [dismissed, setDismissed] = React.useState(false)

  if (!businessInfo) return null

  const fields   = checkFields(businessInfo)
  const filled   = fields.filter(f => f.filled).length
  const percent  = Math.round((filled / fields.length) * 100)

  if (percent >= 100) return null

  const missingBlocking    = fields.filter(f => f.blocking && !f.filled)
  const missingEnrichment  = fields.filter(f => !f.blocking && !f.filled)
  const isBlocking         = missingBlocking.length > 0

  if (!isBlocking && dismissed) return null

  const bg     = 'rgba(224,90,43,0.07)'
  const border = isBlocking ? 'rgba(224,90,43,0.25)' : 'rgba(224,90,43,0.18)'

  return (
    <div style={{ padding: '16px 28px 0' }}>
      <div
        role="alert"
        style={{
          background: bg,
          border: `1px solid ${border}`,
          borderLeft: '3px solid var(--accent)',
          borderRadius: 14,
          padding: '14px 20px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        }}
      >
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
            {isBlocking
              ? 'Finish setting up to activate AI emails'
              : `Your business profile is ${percent}% complete — AI emails improve with more context`}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px' }}>
            {isBlocking
              ? missingBlocking.map(f => (
                  <span key={f.label} style={{ fontSize: 12, color: 'var(--text2)' }}>○ {f.label}</span>
                ))
              : missingEnrichment.map(f => (
                  <span key={f.label} style={{ fontSize: 12, color: 'var(--text2)' }}>○ {f.label}</span>
                ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <button
            onClick={() => setActivePage('business')}
            className="inline-flex items-center gap-1.5 px-[18px] py-[7px] rounded-full text-[12.5px] font-bold text-white bg-[#e05a2b] border border-black shadow-[2px_2px_0_#111] transition-all duration-150 whitespace-nowrap cursor-pointer
              hover:bg-[#c44a1e] hover:-translate-x-[1px] hover:-translate-y-[1px] hover:shadow-[3px_3px_0_#111]
              active:translate-x-[1px] active:translate-y-[1px] active:shadow-[1px_1px_0_#111]"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {isBlocking ? 'Complete setup' : 'Fill in profile'}
          </button>
          {!isBlocking && (
            <button
              onClick={() => setDismissed(true)}
              aria-label="Dismiss"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text3)',
                padding: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 6,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
